import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  Youtube,
} from "lucide-react";
import { useAction, useQuery } from "convex/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Id } from "~/convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { ScriptGeneration } from "./ScriptGeneration";
import { api } from "~/convex/_generated/api";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

type FlowStage = "input" | "loading" | "analysis" | "script-generation";

interface Hook {
  text: string;
  category: string;
  reasoning: string;
  potentialPaths?: string[];
}

export const YouTubeCreatorFlow = () => {
  const { toast } = useToast();
  const [stage, setStage] = useState<FlowStage>("input");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [currentHookIndex, setCurrentHookIndex] = useState(0);
  const [selectedHook, setSelectedHook] = useState<Hook | null>(null);
  const [creatorId, setCreatorId] = useState<Id<"exp__creatorProfile"> | null>(
    null,
  );
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [isGeneratingHooks, setIsGeneratingHooks] = useState(false);

  // Convex actions
  const processCreatorProfile = useAction(
    api.exp__creatorInsights.exp__processCreatorProfileYT,
  );
  const generateHooks = useAction(api.generate.exp__generateHooks);

  // Query for creator data
  const creatorData = useQuery(
    api.exp__creatorInsights.exp__getCreatorProfile,
    creatorId ? { creatorId } : "skip",
  );

  console.log(creatorData);

  // Extract handle from YouTube URL
  const extractHandleFromUrl = (url: string): string | null => {
    try {
      // Handle different YouTube URL formats
      // https://youtube.com/@channelname
      // https://www.youtube.com/@channelname
      // https://youtube.com/c/channelname
      // https://www.youtube.com/c/channelname
      // https://youtube.com/channel/UCxxxxx

      const urlObj = new URL(url);
      const pathname = urlObj.pathname;

      // Handle @username format
      if (pathname.includes("@")) {
        const match = pathname.match(/@([^/]+)/);
        return match ? match[1] : null;
      }

      // Handle /c/channelname format
      if (pathname.includes("/c/")) {
        const match = pathname.match(/\/c\/([^/]+)/);
        return match ? match[1] : null;
      }

      // Handle /channel/UCxxxxx format - we can't use this with forHandle API
      if (pathname.includes("/channel/")) {
        toast({
          title: "URL Format Not Supported",
          description:
            "Please use a URL with @username or /c/channelname format",
          variant: "destructive",
        });
        return null;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const handleUrlSubmit = async () => {
    if (!youtubeUrl.trim()) return;

    const handle = extractHandleFromUrl(youtubeUrl);
    if (!handle) {
      toast({
        title: "Invalid URL",
        description:
          "Please enter a valid YouTube channel URL (e.g., https://youtube.com/@channelname)",
        variant: "destructive",
      });
      return;
    }

    setStage("loading");

    try {
      const profileId = await processCreatorProfile({ handle });
      if (profileId) {
        setCreatorId(profileId);
        setStage("analysis");
      }
    } catch (error) {
      console.error("Error processing creator profile:", error);
      toast({
        title: "Analysis Failed",
        description:
          "Failed to analyze the channel. Please check the URL and try again.",
        variant: "destructive",
      });
      setStage("input");
    }
  };

  const handleGenerateHooks = async () => {
    if (!creatorId) return;

    setIsGeneratingHooks(true);
    try {
      const result = await generateHooks({ creatorId });
      setHooks(result.hooks);
      setCurrentHookIndex(0);
      toast({
        title: "Hooks Generated!",
        description: `Generated ${result.hooks.length} unique content hooks for this creator.`,
      });
    } catch (error) {
      console.error("Error generating hooks:", error);
      toast({
        title: "Hook Generation Failed",
        description: "Failed to generate hooks. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingHooks(false);
    }
  };

  const handleStartCreating = (hook: Hook) => {
    setSelectedHook(hook);
    setStage("script-generation");
  };

  const nextHook = () => {
    setCurrentHookIndex((prev) => (prev + 1) % hooks.length);
  };

  const prevHook = () => {
    setCurrentHookIndex((prev) => (prev - 1 + hooks.length) % hooks.length);
  };

  if (stage === "input") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-4">
              <Youtube className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">YouTube Creator Analysis</CardTitle>
            <p className="text-muted-foreground">
              Enter a YouTube channel URL to get AI-powered insights and script
              hooks
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">YouTube Channel URL</label>
              <Input
                placeholder="https://youtube.com/@channelname"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="w-full"
              />
            </div>
            <Button
              onClick={handleUrlSubmit}
              variant={"destructive"}
              className="w-full"
              disabled={!youtubeUrl.trim()}
            >
              Analyze Channel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (stage === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <div className="mx-auto w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Analyzing Channel</h2>
            <p className="text-muted-foreground mb-4">
              We're analyzing the content, audience, and performance metrics...
            </p>
            <div className="space-y-2 text-sm text-left">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Fetching channel data</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Analyzing video content</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <span>Generating AI insights</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-muted rounded-full"></div>
                <span>Creating content hooks</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (stage === "analysis") {
    if (!creatorData) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <Card className="w-full max-w-md text-center">
            <CardContent className="pt-6">
              <div className="mx-auto w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                Loading Creator Data
              </h2>
              <p className="text-muted-foreground">
                This may take a few moments...
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    const isGeneratingInsights =
      !creatorData.contentNiche ||
      !creatorData.topContentTopics ||
      !creatorData.contentStyle ||
      !creatorData.audienceAnalysis;

    return (
      <div className="p-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => setStage("input")}
              className="mb-4"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Search
            </Button>
            <h1 className="text-3xl font-bold">Creator Analysis</h1>
          </div>

          <div className="space-y-6">
            {/* Creator Info Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-6">
                  {creatorData.image && (
                    <img
                      src={creatorData.image}
                      alt={creatorData.name}
                      className="w-20 h-20 rounded-full flex-shrink-0"
                    />
                  )}
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">
                      {creatorData.name}
                    </CardTitle>
                    <p className="text-muted-foreground mb-4">
                      {creatorData.bio}
                    </p>
                    <div className="flex items-center space-x-6">
                      <div className="text-center">
                        <div className="text-xl font-bold">
                          {new Intl.NumberFormat("en-US", {
                            notation: "compact",
                          }).format(creatorData.followers)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Subscribers
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold">
                          {new Intl.NumberFormat("en-US", {
                            notation: "compact",
                          }).format(creatorData.videos)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Videos
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isGeneratingInsights ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-red-500" />
                    <p className="text-lg font-semibold">
                      Generating Creator Insights
                    </p>
                    <p className="text-muted-foreground">
                      Analyzing content style, topics, and audience patterns...
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="content-niche">
                        <AccordionTrigger>Content Niche</AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-wrap gap-2">
                            {creatorData.contentNiche?.map((niche, index) => (
                              <Badge key={index} variant="secondary">
                                {niche}
                              </Badge>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="top-topics">
                        <AccordionTrigger>Top Content Topics</AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-wrap gap-2">
                            {creatorData.topContentTopics?.map(
                              (topic, index) => (
                                <Badge key={index} variant="outline">
                                  {topic}
                                </Badge>
                              ),
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="content-style">
                        <AccordionTrigger>Content Style</AccordionTrigger>
                        <AccordionContent>
                          <p className="text-sm">{creatorData.contentStyle}</p>
                        </AccordionContent>
                      </AccordionItem>

                      <AccordionItem value="audience">
                        <AccordionTrigger>Audience Analysis</AccordionTrigger>
                        <AccordionContent>
                          <p className="text-sm">
                            {creatorData.audienceAnalysis}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Hooks Card - Only show when insights are ready */}
            {!isGeneratingInsights && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    AI-Generated Hooks
                    {hooks.length > 0 && (
                      <Badge variant="secondary">
                        {currentHookIndex + 1} of {hooks.length}
                      </Badge>
                    )}
                  </CardTitle>
                  <p className="text-muted-foreground">
                    Generate AI-powered hooks based on this creator's style
                  </p>
                </CardHeader>
                <CardContent>
                  {hooks.length === 0 ? (
                    <div className="text-center py-8">
                      <Button
                        onClick={handleGenerateHooks}
                        disabled={isGeneratingHooks}
                        className="w-full max-w-sm"
                      >
                        {isGeneratingHooks ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating Hooks...
                          </>
                        ) : (
                          "Generate Hooks"
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="p-6 rounded-lg border-2 border-dashed min-h-[200px] flex items-center">
                        <div className="w-full">
                          <p className="text-lg font-medium leading-relaxed mb-4">
                            {hooks[currentHookIndex].text}
                          </p>
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-sm text-muted-foreground">
                              {hooks[currentHookIndex].reasoning}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4 mt-4">
                        <div className="flex items-center justify-between">
                          <Button variant="outline" onClick={prevHook}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>

                          <Button
                            onClick={() =>
                              handleStartCreating(hooks[currentHookIndex])
                            }
                            variant="success"
                          >
                            Start Creating
                          </Button>

                          <Button variant="outline" onClick={nextHook}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="flex justify-center">
                          <Button
                            variant="outline"
                            onClick={handleGenerateHooks}
                            disabled={isGeneratingHooks}
                            className="text-sm"
                          >
                            {isGeneratingHooks ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                Regenerating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3 mr-2" />
                                Generate New Set of Hooks
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (stage === "script-generation") {
    if (!creatorId) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <Card className="w-full max-w-md text-center">
            <CardContent className="pt-6">
              <h2 className="text-xl font-semibold mb-2">Error</h2>
              <p className="text-muted-foreground">
                Creator data not found. Please go back and try again.
              </p>
              <Button onClick={() => setStage("input")} className="mt-4">
                Back to Start
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <ScriptGeneration
        selectedHook={selectedHook}
        creatorId={creatorId}
        onBack={() => setStage("analysis")}
      />
    );
  }

  return null;
};
